
import { AutoComplete, AutoCompleteProps, Button, Input, Layout, Table, TableColumnsType, TablePaginationConfig, TableProps } from 'antd';
import { EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import AntDialog from '../../dialogs/dialog';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FinancialLinesService } from '../../services/financialLines';
import { useToast } from '../../hooks/toast';
import { useAuth } from '../../../Hooks/useAuth';

const { Content } = Layout;

interface DataType {
    key: React.Key;
    bank: string;
    titleOfLine: string;
    typeOfFinance: string;
    status: string;
    actions?: React.ReactNode
}

const alphabet = [ ...'abcdefghijklmnopqrstuvwxyz' ];

const columns: TableColumnsType<DataType> = [
    {
        title: 'Banco',
        dataIndex: 'bank',
        showSorterTooltip: { target: 'full-header' },
        filters: [
            {
                text: 'FINEP',
                value: 'FINEP'
            },
            {
                text: 'BNDES',
                value: 'BNDES'
            }
        ],
        onFilter: (value, record) => record.bank.indexOf(value as string) === 0,
        sorter: (a, b) => {
            return alphabet.indexOf(a.bank[ 0 ].toLowerCase()) - alphabet.indexOf(b.bank[ 0 ].toLowerCase())
        },
    },
    {
        title: 'Título da linha',
        dataIndex: 'titleOfLine',
        sorter: (a, b) => a.titleOfLine.length - b.titleOfLine.length,
    },
    {
        title: 'Tipo de financiamento',
        dataIndex: 'typeOfFinance',
        filters: [
            {
                text: 'Subvenção',
                value: 'Subvenção',
            },
            {
                text: 'Reembolsável',
                value: 'Reembolsável',
            }
        ],
        onFilter: (value, record) => record.typeOfFinance.indexOf(value as string) === 0,
        sorter: (a, b) => a.typeOfFinance.length - b.typeOfFinance.length,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        filters: [
            {
                text: 'Ativo',
                value: 'Ativo',
            },
            {
                text: 'Desativado',
                value: 'Desativado',
            }
        ],
        onFilter: (value, record) => record.status.indexOf(value as string) === 0,
        sorter: (a, b) => a.status.length - b.status.length,
    },
    {
        title: 'Ações',
        dataIndex: 'actions',
    },
];

export default function FinancialLines () {

    const [ options, setOptions ] = useState<AutoCompleteProps[ 'options' ]>([]);
    const [ loadingSearch, setLoadingSearch ] = useState<boolean>(false);
    const [ selectFinancial, setSelectFinancial ] = useState<boolean>(false);
    const [ selectDisableLine, setSelectDisableLine ] = useState<boolean>(false);
    const [ list, setList ] = useState([])
    const [ idDisable, setIdDisable ] = useState<{ [ key: string ]: any }>({});
    const [ pagination, setPagination ] = useState<TablePaginationConfig>({ pageSize: 7 })

    const navigate = useNavigate();
    const authData = useAuth()
    const toast = useToast();

    const handleSearch = (value: string) => {
        updateTableFinancialLines(pagination.current, value)
    };

    const redirectRefundable = (id?: string) => {
        navigate(`/finance/financialLines/refundable`, id ? { state: { id } } : {})
    }

    const redirectSubvention = (id?: string) => {
        navigate(`/finance/financialLines/subvention`, id ? { state: { id } } : {})
    }

    const createInteractionButtons = ({ objectData }: { objectData: { [ key: string ]: any } }) => {

        const { id, typeOfFinancing, isVisible } = objectData

        return (
            <div style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'space-around' }}>
                <EditOutlined onClick={() => typeOfFinancing === 0 ? redirectSubvention(id) : redirectRefundable(id)} style={{ color: "#0958D9", fontSize: '20px' }} />
                {
                    isVisible
                        ? <EyeOutlined onClick={() => { setIdDisable(objectData); setSelectDisableLine(true) }} style={{ color: "#0958D9", fontSize: '20px' }} />
                        : <EyeInvisibleOutlined onClick={() => { setIdDisable(objectData); setSelectDisableLine(true) }} style={{ fontSize: '20px', color: '#000000A6' }} />
                }
            </div>
        )
    }

    const updateVisibleFinancialLine = async () => {
        try {
            setLoadingSearch(true)

            if (idDisable.typeOfFinancing === 0) {

                const { data } = await FinancialLinesService.getSubvetion(idDisable.id)

                await FinancialLinesService.putSubvention({
                    id: idDisable.id,
                    userEmail: authData.userEmail,
                    goal: data.goal,
                    value: data.value,
                    counterpart: data.counterpart,
                    typeOfBeneficiary: data.typeOfBeneficiary,
                    deadlines: data.deadlines,
                    bankName: idDisable.bankName,
                    title: idDisable.title,
                    linkLearnMore: idDisable.linkLearnMore,
                    isVisible: !data.financingLines.isVisible,
                    coverage: idDisable.coverage
                })
            } else {

                const { data } = await FinancialLinesService.getRefundable(idDisable.id)

                await FinancialLinesService.putRefundable({
                    id: idDisable.id,
                    title: idDisable.title,
                    summary: data.summary,
                    bankName: idDisable.bankName,
                    coverage: idDisable.coverage,
                    totalTerm: idDisable.totalTerm,
                    userEmail: authData.userEmail,
                    gracePeriods: data.gracePeriods,
                    canBeFinanced: data.canBeFinanced,
                    interestRates: data.interestRates,
                    linkLearnMore: data.financingLines.linkLearnMore,
                    participationBank: data.participationBank,
                    additionalInformation: data.additionalInformation,
                    isVisible: !data.financingLines.isVisible,
                    companySize: data.companySize,
                    sectors: data.sectors
                })
            }
        } catch (err) {
            toast.error({ message: 'algo deu errado ao mudar o status, tente novamente mais tarde!', err })
        } finally {
            setLoadingSearch(false)
        }
    }

    const updateTableFinancialLines = async (page?: number, word?: string) => {
        try {
            setLoadingSearch(true)
            
            const { data } = await FinancialLinesService.getFinancialLinesList(page, word)

            const mappedData = data.items.map((e: any, i: number) => {
                return {
                    key: i,
                    bank: e.bankName,
                    titleOfLine: e.title,
                    typeOfFinance: e.typeOfFinancing === 0 ? 'Subvenção' : 'Reembolsável',
                    status: e.isVisible ? 'Ativo' : 'Desativado',
                    actions: createInteractionButtons({ objectData: e })
                }
            })

            setList(mappedData)
            setPagination({ ...pagination, total: data.total })
        } catch (err) {
            console.log(err)
            toast.error({ message: 'algo deu errado na requisição de listagem, tente novamente' })
        } finally {
            setLoadingSearch(false)
        }
    }

    const onChange: TableProps<DataType>[ 'onChange' ] = (pagination, filters, sorter, extra) => {
        updateTableFinancialLines(pagination.current)
    };

    useEffect(() => {
        updateTableFinancialLines()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>

            {/* DIALOG FOR CREATING NEW FINANCIAL LINE */}
            <AntDialog open={selectFinancial} setOpen={setSelectFinancial}>
                <AntDialog.Header>
                    <Typography style={{ fontWeight: 'bolder', fontSize: '15px', margin: '5px 0px 0px 10px' }}>Selecione Tipo de Linha de Financiamento</Typography>
                </AntDialog.Header>
                <AntDialog.Body>
                    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                        <Button onClick={() => redirectRefundable()} style={{ margin: '0px 10px', background: '#0000A4', color: 'white', width: '244px', height: '40px' }}>Reembolsável</Button>
                        <Button onClick={() => redirectSubvention()} style={{ margin: '0px 10px', background: '#0000A4', color: 'white', width: '244px', height: '40px' }}>Subvenção</Button>
                    </div>
                </AntDialog.Body>
            </AntDialog>

            {/* DIALOG TO CONFIRM DISABLE/ENABLE OF A FINANCIAL LINE */}
            <AntDialog open={selectDisableLine} setOpen={setSelectDisableLine}>
                <AntDialog.Header>
                    <Typography style={{ fontWeight: 'bolder', fontSize: '15px', display: 'flex', margin: '5px 0px 0px 10px' }}>Tem certeza que deseja &nbsp;<Typography fontWeight={'bolder'} fontSize={'15px'} color={'red'}>ativar/desativar</Typography>&nbsp; a linha:</Typography>
                </AntDialog.Header>
                <AntDialog.Body>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                        <Typography style={{ margin: '5px 0px 15px 0px' }}>{`${idDisable.bankName} - ${idDisable.title}`}</Typography>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                        <Button onClick={() => { updateVisibleFinancialLine(); setSelectDisableLine(false) }} style={{ margin: '0px 10px', background: '#0000A4', color: 'white', width: '244px', height: '40px' }}>Sim</Button>
                        <Button onClick={() => setSelectDisableLine(false)} style={{ margin: '0px 10px', background: '#0000A4', color: 'white', width: '244px', height: '40px' }}>Não</Button>
                    </div>
                </AntDialog.Body>
            </AntDialog>

            <Layout style={{ margin: '16px 0px 0px 0px', background: 'transparent' }}>
                <Content>
                    <div style={{
                        padding: 24,
                        minHeight: 380,
                        background: 'white',
                        borderRadius: '14px',
                        height: '100%',
                        marginBottom: '20px'
                    }}>

                        <div style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', width: '100%' }}>
                                <AutoComplete
                                    popupMatchSelectWidth={true}
                                    style={{ width: '70%' }}
                                    options={options}
                                    onSearch={handleSearch}
                                >
                                    <Input.Search color='#0000A4' loading={loadingSearch} placeholder="Procurar" enterButton />
                                </AutoComplete>
                                <Button
                                    type="primary"
                                    icon={<PlusCircleOutlined />}
                                    style={{ margin: '0px 10px', width: '30%', background: '#0000A4' }}
                                    onClick={() => setSelectFinancial(true)}
                                >
                                    Adicionar nova linha
                                </Button>
                            </div>

                            <div style={{ margin: '25px 0px 0px 0px' }}>
                                <Table
                                    columns={columns}
                                    dataSource={list}
                                    loading={loadingSearch}
                                    pagination={pagination}
                                    onChange={onChange}
                                    showSorterTooltip={{ target: 'sorter-icon' }}
                                />
                            </div>

                        </div>

                    </div>
                </Content>
            </Layout>
        </>
    )
} 